import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Tooltip,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import DevicesIcon from '@mui/icons-material/Devices';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import ShieldIcon from '@mui/icons-material/Shield';
import RadarIcon from '@mui/icons-material/Radar';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import AppsIcon from '@mui/icons-material/Apps';
import UsbIcon from '@mui/icons-material/Usb';
import StorageIcon from '@mui/icons-material/Storage';
import PolicyIcon from '@mui/icons-material/Policy';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GavelIcon from '@mui/icons-material/Gavel';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TuneIcon from '@mui/icons-material/Tune';
import ArticleIcon from '@mui/icons-material/Article';
import BackupIcon from '@mui/icons-material/Backup';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import WebhookIcon from '@mui/icons-material/Webhook';
import ExtensionIcon from '@mui/icons-material/Extension';
import CodeIcon from '@mui/icons-material/Code';
import SettingsIcon from '@mui/icons-material/Settings';

import { selectSidebarOpen } from '../features/ui/uiSlice';

const EXPANDED_WIDTH = 260;
const MINI_WIDTH = 64;

const navGroups = [
  {
    label: 'Overview',
    tourId: 'nav-overview',
    collapsible: false,
    items: [
      { text: 'Dashboard',          icon: <DashboardIcon fontSize="small" />, path: '/' },
      { text: 'Analytics',          icon: <BarChartIcon fontSize="small" />,  path: '/advanced-analytics', status: 'Deferred' },
    ],
  },
  {
    label: 'Endpoints',
    tourId: 'nav-endpoints',
    collapsible: true,
    items: [
      { text: 'Devices',            icon: <DevicesIcon fontSize="small" />,        path: '/devices' },
      { text: 'Deployments',        icon: <CloudUploadIcon fontSize="small" />,    path: '/deployments' },
      { text: 'Remote Management',  icon: <DesktopWindowsIcon fontSize="small" />, path: '/remote' },
      { text: 'Updates',            icon: <SystemUpdateIcon fontSize="small" />,   path: '/updates' },
    ],
  },
  {
    label: 'Threat Defense',
    tourId: 'nav-threat-defense',
    collapsible: true,
    items: [
      { text: 'EDR',                icon: <ShieldIcon fontSize="small" />,      path: '/edr' },
      { text: 'Threat Intelligence',icon: <RadarIcon fontSize="small" />,       path: '/threat-intelligence', status: 'Deferred' },
      { text: 'DLP',                icon: <LockIcon fontSize="small" />,        path: '/dlp', status: 'Prototype' },
      { text: 'Forensics',          icon: <SearchIcon fontSize="small" />,      path: '/forensics', status: 'Prototype' },
      { text: 'Remediation',        icon: <BuildIcon fontSize="small" />,       path: '/remediation', status: 'Deferred' },
      { text: 'App Control',        icon: <AppsIcon fontSize="small" />,        path: '/application-control', status: 'Deferred' },
      { text: 'Device Control',     icon: <UsbIcon fontSize="small" />,         path: '/device-control', status: 'Deferred' },
      { text: 'NAS Protection',     icon: <StorageIcon fontSize="small" />,     path: '/nas-protection', status: 'Deferred' },
    ],
  },
  {
    label: 'Compliance & Policy',
    tourId: 'nav-compliance',
    collapsible: true,
    items: [
      { text: 'Policies',           icon: <PolicyIcon fontSize="small" />,       path: '/policies' },
      { text: 'Security',           icon: <VerifiedUserIcon fontSize="small" />, path: '/security' },
      { text: 'Compliance',         icon: <GavelIcon fontSize="small" />,        path: '/compliance' },
      { text: 'Health',             icon: <MonitorHeartIcon fontSize="small" />, path: '/health' },
    ],
  },
  {
    label: 'Operations',
    tourId: 'nav-operations',
    collapsible: true,
    items: [
      { text: 'Reports',            icon: <AssessmentIcon fontSize="small" />, path: '/reports', status: 'Deferred' },
      { text: 'Configurations',     icon: <TuneIcon fontSize="small" />,       path: '/configurations' },
      { text: 'Logs',               icon: <ArticleIcon fontSize="small" />,    path: '/logs', status: 'Deferred' },
      { text: 'Backups',            icon: <BackupIcon fontSize="small" />,     path: '/backups', status: 'Deferred' },
    ],
  },
  {
    label: 'Administration',
    tourId: 'nav-administration',
    collapsible: true,
    items: [
      { text: 'Users',              icon: <GroupIcon fontSize="small" />,    path: '/users' },
      { text: 'Tenant Management',  icon: <BusinessIcon fontSize="small" />, path: '/tenant-management' },
      { text: 'API Keys',           icon: <VpnKeyIcon fontSize="small" />,   path: '/settings/api-keys' },
      { text: 'Webhooks',           icon: <WebhookIcon fontSize="small" />,  path: '/webhooks', status: 'Deferred' },
      { text: 'Integrations',       icon: <ExtensionIcon fontSize="small" />,path: '/integrations', status: 'Deferred' },
      { text: 'API Docs',           icon: <CodeIcon fontSize="small" />,     path: '/api-docs' },
      { text: 'Settings',           icon: <SettingsIcon fontSize="small" />, path: '/settings' },
    ],
  },
];

function NavItem({ item, open, isActive }) {
  const navigate = useNavigate();

  const button = (
    <ListItemButton
      selected={isActive}
      onClick={() => navigate(item.path)}
      sx={{ justifyContent: open ? 'flex-start' : 'center', px: open ? 1.25 : 1 }}
    >
      <ListItemIcon sx={{ minWidth: open ? 36 : 'unset', justifyContent: 'center' }}>
        {item.icon}
      </ListItemIcon>
      {open && <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.84rem', fontWeight: isActive ? 600 : 400 }} />}
      {open && item.status && (
        <Chip
          label={item.status}
          size="small"
          color={item.status === 'Prototype' ? 'warning' : 'default'}
          variant="outlined"
          sx={{ height: 18, fontSize: '0.62rem', ml: 1 }}
        />
      )}
    </ListItemButton>
  );

  if (!open) {
    return (
      <ListItem disablePadding>
        <Tooltip title={item.text} placement="right">
          {button}
        </Tooltip>
      </ListItem>
    );
  }

  return <ListItem disablePadding>{button}</ListItem>;
}

function Sidebar() {
  const location = useLocation();
  const open = useSelector(selectSidebarOpen);

  // All collapsible groups start open
  const [groupOpen, setGroupOpen] = useState(
    navGroups.reduce((acc, g) => ({ ...acc, [g.label]: true }), {})
  );

  const toggleGroup = (label) => {
    setGroupOpen((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? EXPANDED_WIDTH : MINI_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? EXPANDED_WIDTH : MINI_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      {/* Logo area */}
      <Box
        data-tour="sidebar-logo"
        sx={{
          px: open ? 2 : 1,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 64,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SecurityIcon sx={{ fontSize: 18, color: '#000' }} />
        </Box>
        {open && (
          <Box>
            <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
              SecurePoint
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: '0.1em', fontSize: '0.6rem' }}>
              CENTRAL
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation groups */}
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, py: 1 }}>
        {navGroups.map((group) => (
          <Box key={group.label} data-tour={group.tourId}>
            {/* Group header */}
            {open ? (
              group.collapsible ? (
                <Box
                  onClick={() => toggleGroup(group.label)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2.5,
                    pt: 2,
                    pb: 0.5,
                    cursor: 'pointer',
                    '&:hover': { '& .MuiTypography-root': { color: 'text.primary' } },
                  }}
                >
                  <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>
                    {group.label}
                  </Typography>
                  <IconButton size="small" sx={{ color: 'text.disabled', p: 0 }}>
                    {groupOpen[group.label] ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ px: 2.5, pt: 2, pb: 0.5 }}>
                  <Typography variant="overline" sx={{ color: 'text.disabled', fontSize: '0.62rem' }}>
                    {group.label}
                  </Typography>
                </Box>
              )
            ) : (
              <Box sx={{ height: 8 }} /> // spacer in mini mode
            )}

            {/* Group items */}
            <Collapse in={!group.collapsible || groupOpen[group.label]} timeout="auto">
              <List disablePadding>
                {group.items.map((item) => (
                  <NavItem key={item.path} item={item} open={open} isActive={isActive(item.path)} />
                ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </Box>
    </Drawer>
  );
}

export default Sidebar;
